import { Stack, Typography } from '@mui/material';
import { first } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { TextInput } from '@/components/form/baseInputs/TextInput/TextInput';
import { ChevronDownIcon } from '@/components/icons/ChevronDownIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { LayoutAlt04Icon } from '@/components/icons/LayoutAlt04Icon';
import { SearchMdIcon } from '@/components/icons/SearchMdIcon';
import { XCloseIcon } from '@/components/icons/XCloseIcon';
import {
  AddItemStack,
  AddItemStackItem,
} from '@/components/layout/AddItemStack/AddItemStack';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import {
  getNewBundleId,
  getNewPageId,
} from '@/modules/presentation/clientPresentation/ClientPresentationDesigner.utils';
import { useAiInsightsEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAiInsightsEnabled';
import { COLORS } from '@/styles/tokens/colors';
import { PresentationBundleKind, PresentationPageKind } from '@/types/schema';

import {
  BORDER_COLOR,
  LEFT_SIDEBAR_WIDTH_PX,
} from '../../PresentationTemplateEditor.constants';
import {
  useBundleKindRequiresConfigModal,
  usePresentationTemplateEditorContext,
} from '../../PresentationTemplateEditor.context';
import { PresentationTemplateEditorPage } from '../../PresentationTemplateEditor.types';
import { getTemplateBundleDisplayName } from './PresentationTemplateStageEditor.utils';

type AddBundleValue = Pick<AddPageGroupItemConfig, 'kind' | 'pages'>;

export function PresentationTemplateStageEditorSidebar() {
  const [searchTerm, setSearchTerm] = useState('');
  const handleAddBundle = useAddBundleHandler();
  const aiInsightsEnabled = useAiInsightsEnabled();
  const businessesOverviewSlideEnabled = useFeatureFlag(
    'presentation_businesses_overview_slide'
  );

  const availablePageGroups = useMemo(
    () =>
      PAGE_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          isTemplateBundleAvailable(item.kind, {
            aiInsightsEnabled,
            businessesOverviewSlideEnabled,
          })
        ),
      })),
    [aiInsightsEnabled, businessesOverviewSlideEnabled]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );
  const handleClearSearch = useCallback(() => setSearchTerm(''), []);

  const trimmedSearch = searchTerm.trim().toLowerCase();
  const searchResults = useMemo<
    AddItemStackItem<AddBundleValue>[] | null
  >(() => {
    if (!trimmedSearch) return null;
    return availablePageGroups
      .flatMap((group) => group.items.map(toStackItem))
      .filter((item) => item.label.toLowerCase().includes(trimmedSearch));
  }, [availablePageGroups, trimmedSearch]);

  return (
    <Stack
      direction="column"
      flex={`0 0 ${LEFT_SIDEBAR_WIDTH_PX}px`}
      borderRight={`1px solid ${BORDER_COLOR}`}
      p={2}
      spacing={2}
      overflow="auto"
    >
      <Stack direction="column" spacing={1}>
        <TopText />
        <TextInput
          label="Search pages"
          hideLabel
          name="sidebar-search"
          placeholder="Search pages"
          value={searchTerm}
          onChange={handleSearchChange}
          startAdornment={<SearchMdIcon size={16} color={COLORS.NAVY[300]} />}
          endAdornment={
            searchTerm ? (
              <IconButton
                icon={XCloseIcon}
                ariaLabel="Clear search"
                variant="transparent"
                size="xs"
                onClick={handleClearSearch}
              />
            ) : undefined
          }
        />
      </Stack>
      {searchResults ? (
        <SearchResults items={searchResults} onClick={handleAddBundle} />
      ) : (
        availablePageGroups.map((group) => (
          <CollapsibleAddPageGroup
            key={group.label}
            {...group}
            onAddBundle={handleAddBundle}
          />
        ))
      )}
    </Stack>
  );
}

export interface TemplateBundleAvailability {
  aiInsightsEnabled: boolean;
  businessesOverviewSlideEnabled: boolean;
}

export function isTemplateBundleAvailable(
  bundleKind: PresentationBundleKind,
  {
    aiInsightsEnabled,
    businessesOverviewSlideEnabled,
  }: TemplateBundleAvailability
): boolean {
  if (
    bundleKind === PresentationBundleKind.EstateInsightsBundle ||
    bundleKind === PresentationBundleKind.PlanningOpportunitiesBundle
  ) {
    return aiInsightsEnabled;
  }
  if (bundleKind === PresentationBundleKind.BusinessOverviewBundle) {
    return businessesOverviewSlideEnabled;
  }
  return true;
}

function TopText() {
  return (
    <Stack direction="column" spacing={0.5}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <LayoutAlt04Icon size={16} color={COLORS.TEXT.SECONDARY} />
        <Typography variant="title-s">Add pages</Typography>
      </Stack>
      <Typography variant="body-xs">
        Click the pages you want to add to your template
      </Typography>
    </Stack>
  );
}

interface AddPageGroupProps {
  label: string;
  items: AddPageGroupItemConfig[];
}

interface AddPageGroupItemConfig {
  kind: PresentationBundleKind;
  pages?: PresentationPageKind[];
  disabled?: boolean;
}

function toStackItem({
  kind,
  pages,
  disabled,
}: AddPageGroupItemConfig): AddItemStackItem<AddBundleValue> {
  return {
    label: getTemplateBundleDisplayName(kind, first(pages)),
    value: { kind, pages },
    disabled,
  };
}

function CollapsibleAddPageGroup({
  label,
  items,
  onAddBundle,
}: AddPageGroupProps & {
  onAddBundle: (value: AddBundleValue) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const stackItems = useMemo(() => items.map(toStackItem), [items]);
  const toggle = useCallback(() => setIsExpanded((prev) => !prev), []);

  const ChevronIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon;

  return (
    <Stack direction="column" spacing={1}>
      <Stack
        component="button"
        type="button"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${label}`}
        onClick={toggle}
        sx={{
          width: '100%',
          background: 'none',
          border: 0,
          p: 0,
          font: 'inherit',
          color: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <Typography variant="title-xs">{label}</Typography>
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ px: 1, py: '5px' }}
        >
          <ChevronIcon size={16} />
        </Stack>
      </Stack>
      {isExpanded && <AddItemStack items={stackItems} onClick={onAddBundle} />}
    </Stack>
  );
}

function SearchResults({
  items,
  onClick,
}: {
  items: AddItemStackItem<AddBundleValue>[];
  onClick: (value: AddBundleValue) => void;
}) {
  if (items.length === 0) {
    return (
      <Typography variant="body-xs" color="textSecondary">
        No matching pages.
      </Typography>
    );
  }
  return <AddItemStack items={items} onClick={onClick} />;
}

function useAddBundleHandler() {
  const {
    bundlesFormArray: { append },
    openBundleConfigModal,
  } = usePresentationTemplateEditorContext();
  const bundleKindRequiresConfigModal = useBundleKindRequiresConfigModal();

  return useCallback(
    ({ kind, pages }: AddBundleValue) => {
      if (bundleKindRequiresConfigModal(kind)) {
        openBundleConfigModal(kind);
        return;
      }

      const id = getNewBundleId(kind);
      const bundlePages = pages?.map(createPage) || [];
      if (!append) {
        throw new Error('Bundles form array is not initialized');
      }
      append({
        kind,
        pages: bundlePages,
        id,
        dividerTemplateBundleConfiguration:
          kind === PresentationBundleKind.DividerBundle
            ? { id: '', dividerTitle: '', customContent: '' }
            : undefined,
      });
    },
    [append, openBundleConfigModal, bundleKindRequiresConfigModal]
  );
}

function createPage(
  kind: PresentationPageKind
): PresentationTemplateEditorPage {
  return {
    id: getNewPageId(kind),
    kind,
  };
}

const GENERATED_GROUP_LABEL = 'AI-generated pages';

const PAGE_GROUPS: AddPageGroupProps[] = [
  {
    label: 'Standard pages',
    items: [
      {
        kind: PresentationBundleKind.CoverSlideBundle,
        pages: [PresentationPageKind.CoverPage],
      },
      {
        kind: PresentationBundleKind.TableOfContentsBundle,
      },
      {
        kind: PresentationBundleKind.BalanceSheetBundle,
        pages: [PresentationPageKind.BalanceSheetPage],
      },
      {
        kind: PresentationBundleKind.FamilyTreeBundle,
      },
      {
        kind: PresentationBundleKind.EstateInsightsBundle,
      },
      {
        kind: PresentationBundleKind.EntitiesOverviewBundle,
      },
      {
        kind: PresentationBundleKind.EntitySummaryBundle,
      },
      {
        kind: PresentationBundleKind.WaterfallOverviewBundle,
      },
      {
        kind: PresentationBundleKind.BeneficiaryReportBundle,
      },
      {
        kind: PresentationBundleKind.WaterfallComparisonBundle,
      },
      {
        kind: PresentationBundleKind.DocumentSummaryBundle,
      },
      {
        kind: PresentationBundleKind.LifeInsuranceBundle,
      },
      {
        kind: PresentationBundleKind.LiabilitiesBundle,
      },
      {
        kind: PresentationBundleKind.ProfessionalTeamBundle,
      },
      {
        kind: PresentationBundleKind.LuminaryDisclaimerBundle,
      },
      {
        kind: PresentationBundleKind.StandaloneDisclaimerBundle,
      },
      {
        kind: PresentationBundleKind.CustomPageBundle,
        pages: [PresentationPageKind.CustomPage],
      },
      {
        kind: PresentationBundleKind.DividerBundle,
        pages: [PresentationPageKind.Divider],
      },
    ],
  },
  {
    label: GENERATED_GROUP_LABEL,
    items: [
      { kind: PresentationBundleKind.DocumentsReviewedBundle },
      { kind: PresentationBundleKind.FamilyOverviewBundle },
      { kind: PresentationBundleKind.WillBundle },
      { kind: PresentationBundleKind.PowerOfAttorneyBundle },
      { kind: PresentationBundleKind.HealthcareDirectiveBundle },
      { kind: PresentationBundleKind.TrustOverviewBundle },
      { kind: PresentationBundleKind.DiagramBundle },
      { kind: PresentationBundleKind.BusinessOverviewBundle },
      { kind: PresentationBundleKind.AssetDistributionBundle },
      { kind: PresentationBundleKind.PlanningOpportunitiesBundle },
    ],
  },
  {
    label: 'Education pages',
    items: [
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.EstatePlanningGlossary],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.WhyEstatePlanningMatters],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.NoEstatePlanningConsequences],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.SettingUpEstatePlan],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.EstatePlanningChecklist],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.ManagingRecentInheritance],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.EstateTaxesExplainer],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.StateInheritanceTaxExplainer],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.GstExplainer],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.WhyUseTrust],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.TrustBasics],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.PopularPlanningStrategies],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.CltStrategyExplainer],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.CrtStrategyExplainer],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.FlpStrategyExplainer],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.GratStrategyExplainer],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.InstallmentSaleStrategyExplainer],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.IlitStrategyExplainer],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.SlatStrategyExplainer],
      },
      {
        kind: PresentationBundleKind.EducationPageBundle,
        pages: [PresentationPageKind.QprtStrategyExplainer],
      },
    ],
  },
];
